import React, { useEffect, useState } from "react";
import { Inbox, MessageSquareReply, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

const urgency = {
  Critical: "bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/20",
  High: "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/20",
  Normal: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/20",
};

const status = {
  Open: "bg-blue-50 text-blue-700 border-blue-100",
  "In Review": "bg-indigo-50 text-indigo-700 border-indigo-100",
  Responded: "bg-emerald-50 text-emerald-700 border-emerald-100",
  Closed: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function ClientRequests({ refreshKey }) {
  const [items, setItems] = useState([]);
  const load = () => endpoints.listRequests().then(setItems).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  const del = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await endpoints.deleteClientRequest(id); toast.success("Request deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="client-requests-card" className="glass-card rounded-3xl p-5 sm:p-6 h-full card-hover">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-sky-50 dark:bg-sky-500/15 text-sky-600 dark:text-sky-300 flex items-center justify-center">
          <Inbox className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="font-display font-bold text-[15px] tracking-tight">Client Requests</h2>
          <p className="text-xs text-slate-500">Recent inbound</p>
        </div>
        <Badge variant="secondary" className="rounded-full">{items.length}</Badge>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-500">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
            <Inbox className="h-5 w-5 text-slate-400" />
          </div>
          No requests yet. Create one from Quick Actions.
        </div>
      ) : (
        <ScrollArea className="h-[300px] pr-2">
          <ul className="space-y-2.5">
            {items.map((r) => (
              <li key={r.id} data-testid={`request-item-${r.id}`} className="group rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{r.request_type}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {r.client_name}{r.iso_standard ? ` · ${r.iso_standard}` : ""}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge variant="outline" className={`text-[10px] rounded-full ${urgency[r.urgency] || ""}`}>{r.urgency}</Badge>
                    <Badge variant="outline" className={`text-[10px] rounded-full ${status[r.status] || ""}`}>{r.status}</Badge>
                  </div>
                </div>
                {r.note && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{r.note}</p>}
                <div className="flex justify-end gap-1 mt-2">
                  <Button data-testid={`request-respond-${r.id}`} size="sm" variant="ghost" className="h-7 text-xs text-indigo-600 hover:text-indigo-700">
                    <MessageSquareReply className="h-3.5 w-3.5 mr-1" /> Respond
                  </Button>
                  <Button data-testid={`request-delete-${r.id}`} size="sm" variant="ghost" className="h-7 text-xs text-slate-400 hover:text-rose-600"
                    onClick={() => del(r.id)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}
    </div>
  );
}
